import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import Badge from '../../../../components/Atoms/Badge';
import {
  typeMeta,
  isTrained,
  versionLabel,
  formatCount,
  timeAgo,
  formatDate,
  formatMetricLabel,
  latestTrainedVersion,
  ownerInitials,
  ownerHue,
  ownerKey,
} from '../hubMeta';

const MetricRow = ({ label, value }) => (
  <div className="hub-metric">
    <span className="hub-metric__label">{label}</span>
    <span className="hub-metric__value">{value}</span>
  </div>
);

const formatMetric = (v) => {
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(4);
  return String(v);
};

const HubModelDrawer = ({ model, onClose, onDownload, downloadingId }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!model) return null;
  const meta = typeMeta(model.model_type);
  const versions = Array.isArray(model.versions) ? model.versions : [];
  const owner = model.owner_name || 'Unknown';
  const latest = latestTrainedVersion(model);
  const busyLatest = latest && downloadingId === latest._id;

  const copyId = () => {
    try {
      navigator.clipboard?.writeText(model.model_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      /* clipboard unavailable — silently ignore */
    }
  };

  return ReactDOM.createPortal(
    <div className="hub-drawer-overlay" onMouseDown={onClose}>
      <aside
        className="hub-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`${model.model_name} details`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="hub-drawer__header">
          <div className={`hub-card__icon hub-card__icon--${model.model_type}`}>
            <i className={`fa ${meta.icon}`} aria-hidden />
          </div>
          <div className="hub-drawer__heading">
            <h2 className="hub-drawer__title">{model.model_name}</h2>
            <div className="hub-drawer__subrow">
              <span className={`hub-type-pill hub-type-pill--${model.model_type}`}>
                <i className={`fa ${meta.icon}`} aria-hidden /> {meta.label}
              </span>
              {model.created_on && (
                <span className="hub-card__updated" title={`Published ${formatDate(model.created_on)}`}>
                  <i className="fa fa-clock" aria-hidden /> {timeAgo(model.created_on)}
                </span>
              )}
            </div>
          </div>
          <button type="button" className="hub-drawer__close" onClick={onClose} aria-label="Close">
            <i className="fa fa-times" aria-hidden />
          </button>
        </header>

        <div className="hub-drawer__body">
          <p className="hub-drawer__detail">
            {model.model_detail || 'No description provided.'}
          </p>

          {Array.isArray(model.tags) && model.tags.length > 0 && (
            <div className="hub-card__tags">
              {model.tags.map((tag) => (
                <span key={tag} className="hub-tag">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {latest && (
            <button
              type="button"
              className="hub-drawer__primary"
              disabled={busyLatest}
              onClick={() => onDownload(model, latest)}
            >
              {busyLatest ? (
                <i className="fa fa-spinner fa-spin" aria-hidden />
              ) : (
                <i className="fa fa-download" aria-hidden />
              )}{' '}
              Download latest ({versionLabel(latest)})
            </button>
          )}

          <div className="hub-drawer__facts">
            <div className="hub-metric">
              <span className="hub-metric__label">Owner</span>
              <span className="hub-metric__value hub-owner-val">
                <span
                  className="hub-avatar hub-avatar--sm"
                  style={{ background: `hsl(${ownerHue(ownerKey(model))} 55% 42%)` }}
                  aria-hidden
                >
                  {ownerInitials(owner)}
                </span>
                {owner}
              </span>
            </div>
            {model.project_name && <MetricRow label="Project" value={model.project_name} />}
            <MetricRow label="Versions" value={model.version_count ?? versions.length} />
            <MetricRow label="Downloads" value={formatCount(model.download_count)} />
            {model.created_on && <MetricRow label="Published" value={formatDate(model.created_on)} />}
            <div className="hub-metric">
              <span className="hub-metric__label">Model ID</span>
              <span className="hub-metric__value hub-id-val">
                <code className="hub-mono">{model.model_id}</code>
                <button
                  type="button"
                  className="hub-copy-btn"
                  onClick={copyId}
                  title="Copy model ID"
                  aria-label="Copy model ID"
                >
                  <i className={`fa ${copied ? 'fa-check' : 'fa-copy'}`} aria-hidden />
                </button>
              </span>
            </div>
          </div>

          <h3 className="hub-drawer__section-title">
            Versions <span className="hub-drawer__section-count">{versions.length}</span>
          </h3>
          {versions.length === 0 ? (
            <p className="hub-drawer__empty">No versions yet.</p>
          ) : (
            <ul className="hub-version-list">
              {versions.map((ver) => {
                const trained = isTrained(ver);
                const busy = downloadingId === ver._id;
                const classes = Array.isArray(ver.classes) ? ver.classes : [];
                const metrics = ver.performanceMetrics && typeof ver.performanceMetrics === 'object'
                  ? Object.entries(ver.performanceMetrics)
                  : [];
                return (
                  <li key={ver._id} className="hub-version">
                    <div className="hub-version__head">
                      <span className="hub-version__name">
                        <i className="fa fa-code-branch" aria-hidden /> {versionLabel(ver)}
                      </span>
                      {trained ? (
                        <Badge variant="success" icon="fa-check">Trained</Badge>
                      ) : (
                        <Badge variant="warning">Pending</Badge>
                      )}
                      {trained && (
                        <button
                          type="button"
                          className="hub-version__download"
                          disabled={busy}
                          onClick={() => onDownload(model, ver)}
                        >
                          {busy ? (
                            <i className="fa fa-spinner fa-spin" aria-hidden />
                          ) : (
                            <i className="fa fa-download" aria-hidden />
                          )}{' '}
                          Download
                        </button>
                      )}
                    </div>

                    {classes.length > 0 && (
                      <div className="hub-version__classes">
                        {classes.map((c) => (
                          <span key={`${c.id}-${c.name}`} className="hub-class-chip">
                            <span
                              className="hub-class-chip__dot"
                              style={c.color ? { background: c.color } : undefined}
                            />
                            {c.name}
                          </span>
                        ))}
                      </div>
                    )}

                    {metrics.length > 0 && (
                      <div className="hub-version__metrics">
                        {metrics.map(([k, v]) => (
                          <MetricRow key={k} label={formatMetricLabel(k)} value={<span className="hub-mono">{formatMetric(v)}</span>} />
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
};

export default HubModelDrawer;
